import api from '../../utils/api'
import { useState, useEffect } from 'react'
import { FaPencilAlt, FaTrashAlt } from 'react-icons/fa'
import { FiInbox, FiPause, FiPlay } from 'react-icons/fi'

import Button from '../../shared/components/Button/Button'
import Modal from '../../shared/modal/Modal'
import ConfirmDeleteModal from '../../shared/components/ConfirmDeleteModal/ConfirmDeleteModal'
import FormFinanceRecurring from './Form/FormFinanceRecurring'
import useMyPermission from '../../hooks/useMyPermission'
import useFlashMessage from '../../hooks/useFlashMessage'

import './Finance.css'

function RecurringTemplates() {
    const [token] = useState(localStorage.getItem('token') || '')
    const { setFlashMessage } = useFlashMessage()

    const [type, setType] = useState('pagar')
    const [templates, setTemplates] = useState([])
    const [categories, setCategories] = useState([])
    const [load, setLoad] = useState(false)

    const [modalNew, setModalNew] = useState(false)
    const [editingTemplate, setEditingTemplate] = useState(null)
    const [deletingTemplate, setDeletingTemplate] = useState(null)

    /* ---------------------- Permission ------------------------- */
    const [myPermission, setMyPermission] = useState({ group: '' })
    const { checkPermission } = useMyPermission()
    const myPermissionView = checkPermission(myPermission, 'finance_view')
    const myPermissionCreate = checkPermission(myPermission, 'finance_create')
    const myPermissionEdit = checkPermission(myPermission, 'finance_edit')
    const myPermissionDelete = checkPermission(myPermission, 'finance_delete')

    const getAll = () => {
        Promise.all([
            api.get(`/finance/recurring/getall?type=${type}`, { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
            api.get('/finance/category/getall', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
        ]).then(([templateRes, categoryRes]) => {
            setTemplates(templateRes.data.templates || [])
            setMyPermission(templateRes.data.permission)
            setCategories(categoryRes.data.categories || [])
            setLoad(true)
        })
    }

    useEffect(() => {
        getAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, type])

    async function toggleActive(template) {
        let msgType = 'success'
        const data = await api.patch(`/finance/recurring/${template._id}`, { active: !template.active }, { headers: { Authorization: `Bearer ${JSON.parse(token)}` } })
            .then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') getAll()
    }

    async function confirmDelete() {
        let msgType = 'success'
        const data = await api.delete(`/finance/recurring/${deletingTemplate._id}`, { headers: { Authorization: `Bearer ${JSON.parse(token)}` } })
            .then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        setDeletingTemplate(null)
        if (msgType !== 'error') getAll()
    }

    if (!load) {
        return <div className="pg-loading">Carregando contas fixas...</div>
    }

    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Contas Fixas</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-actions">
                            {myPermissionCreate && (
                                <Button variant="new" onClick={() => setModalNew(true)}>Nova Conta Fixa</Button>
                            )}
                        </div>
                        <div className="pg-header-right">
                            <div className="fin-type-tabs">
                                <button type="button" className={`fin-type-tab ${type === 'pagar' ? 'fin-type-tab-active' : ''}`} onClick={() => setType('pagar')}>Pagar</button>
                                <button type="button" className={`fin-type-tab ${type === 'receber' ? 'fin-type-tab-active' : ''}`} onClick={() => setType('receber')}>Receber</button>
                            </div>
                            <span className="pg-count">{templates.length} {templates.length === 1 ? 'conta fixa' : 'contas fixas'}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="pg-list">
                {templates.length > 0 ? templates.map((template) => (
                    <div key={template._id} className="pg-item">
                        <div className="pg-item-main fin-row-main">
                            <div className="fin-row-header">
                                <span className="fin-row-desc">{template.description}</span>
                                {template.counterpart?.name && <span className="fin-row-meta">{template.counterpart.name}</span>}
                            </div>
                            <div className="fin-row-categories">
                                {template.allocations.map((a, i) => a.category && (
                                    <span key={i} className="fin-tag">{a.category.name}</span>
                                ))}
                            </div>
                        </div>

                        <div className="fin-row-value-col">
                            <span className="fin-row-value">R$ {template.totalValue.toFixed(2)}</span>
                            <span className="fin-row-due">todo dia {template.dayOfMonth}</span>
                            <span className={`fin-status ${template.active ? 'fin-status-pago' : 'fin-status-cancelado'}`}>{template.active ? 'Ativa' : 'Pausada'}</span>
                        </div>

                        <div className="shd-row-box-item-action">
                            {myPermissionEdit && (
                                template.active
                                    ? <FiPause className="shd-icon-row-edit" title="Pausar" onClick={() => toggleActive(template)} />
                                    : <FiPlay className="shd-icon-row-edit" title="Reativar" onClick={() => toggleActive(template)} />
                            )}
                            {myPermissionEdit && (
                                <FaPencilAlt className="shd-icon-row-edit" title="Editar" onClick={() => setEditingTemplate(template)} />
                            )}
                            {myPermissionDelete && (
                                <FaTrashAlt className="shd-icon-row-trash" title="Apagar" onClick={() => setDeletingTemplate(template)} />
                            )}
                        </div>
                    </div>
                )) : (
                    <div className="pg-empty">
                        <FiInbox size={32} className="pg-empty-icon" />
                        <p className="pg-empty-title">Nenhuma conta fixa cadastrada</p>
                        <p className="pg-empty-subtitle">Ex.: aluguel, luz, internet — o lançamento do mês é gerado sozinho todo dia do vencimento escolhido.</p>
                    </div>
                )}
            </div>

            <Modal
                modalStatus={modalNew}
                changeStatus={setModalNew}
                w={70}
                content={
                    <FormFinanceRecurring
                        type={type}
                        categories={categories}
                        btCancelAction={() => setModalNew(false)}
                        handleOnSuccess={() => { setModalNew(false); getAll() }}
                    />
                }
            />

            <Modal
                modalStatus={!!editingTemplate}
                changeStatus={() => setEditingTemplate(null)}
                w={70}
                content={
                    editingTemplate && (
                        <FormFinanceRecurring
                            type={type}
                            categories={categories}
                            editingTemplate={editingTemplate}
                            btCancelAction={() => setEditingTemplate(null)}
                            handleOnSuccess={() => { setEditingTemplate(null); getAll() }}
                        />
                    )
                }
            />

            <ConfirmDeleteModal
                modalStatus={!!deletingTemplate}
                changeStatus={() => setDeletingTemplate(null)}
                onConfirm={confirmDelete}
                message="Deseja realmente apagar essa conta fixa? Os lançamentos já gerados continuam existindo."
                itemName={deletingTemplate?.description}
            />

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar o Financeiro</p>
        </div>
    )
}

export default RecurringTemplates
